import {
	HttpErrorResponse,
	HttpEvent,
	HttpHandler,
	HttpInterceptor,
	HttpRequest,
	HttpResponse,
} from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";
import { environment } from "../../../../environments/environment";
import { ISpResponse } from "../models/http.interface";

@Injectable()
export class SpResponseInterceptor implements HttpInterceptor {
	private readonly spServerErrorCode = 3;
	private readonly spUserErrorCode = 1;
	private readonly unexpectedResponseMessage = "Ocurrio un error inesperado";
	private readonly apiBasePath = `${environment.urlBackendOCR}/api/`;

	intercept(
		request: HttpRequest<unknown>,
		next: HttpHandler
	): Observable<HttpEvent<unknown>> {
		return next.handle(request).pipe(
			map((event: HttpEvent<unknown>) => {
				if (!this.shouldNormalizeResponse(request, event)) {
					return event;
				}

				const response = event as HttpResponse<unknown>;

				return response.clone({
					body: this.normalizeResponseBody(response.body),
				});
			}),
			catchError((errorHttp: unknown) => {
				if (!this.shouldNormalizeError(request, errorHttp)) {
					return throwError(errorHttp);
				}

				return throwError(this.normalizeHttpError(errorHttp as HttpErrorResponse));
			})
		);
	}

	private shouldNormalizeResponse(
		request: HttpRequest<unknown>,
		event: HttpEvent<unknown>
	): boolean {
		return (
			event instanceof HttpResponse &&
			request.responseType === "json" &&
			this.isProcesarRecibosLuzRequest(request.url) &&
			event.body !== null &&
			event.body !== undefined
		);
	}

	private isProcesarRecibosLuzRequest(url: string): boolean {
		return url.indexOf(this.apiBasePath) === 0;
	}

	private shouldNormalizeError(
		request: HttpRequest<unknown>,
		errorHttp: unknown
	): boolean {
		return (
			errorHttp instanceof HttpErrorResponse &&
			request.responseType === "json" &&
			this.isProcesarRecibosLuzRequest(request.url)
		);
	}

	private normalizeResponseBody<T>(body: unknown): ISpResponse<T> {
		if (this.esEnvolturaRespuestaSp<T>(body)) {
			return this.toSpResponse(body, this.spServerErrorCode);
		}

		return {
			IdResultado: this.spServerErrorCode,
			Mensaje: this.unexpectedResponseMessage,
			data: null as T,
		};
	}

	private normalizeHttpError(errorHttp: HttpErrorResponse): HttpErrorResponse {
		if (this.esEnvolturaRespuestaSp(errorHttp.error)) {
			return new HttpErrorResponse({
				error: this.toSpResponse(errorHttp.error, this.resolveErrorCode(errorHttp.status)),
				headers: errorHttp.headers,
				status: errorHttp.status,
				statusText: errorHttp.statusText,
				url: errorHttp.url || undefined,
			});
		}

		return new HttpErrorResponse({
			error: this.normalizeErrorBody(errorHttp.error, errorHttp.status),
			headers: errorHttp.headers,
			status: errorHttp.status,
			statusText: errorHttp.statusText,
			url: errorHttp.url || undefined,
		});
	}

	private normalizeErrorBody<T>(body: unknown, statusCode: number): ISpResponse<T> {
		if (this.esEnvolturaRespuestaSp<T>(body)) {
			return this.toSpResponse(body, this.resolveErrorCode(statusCode));
		}

		return {
			IdResultado: this.resolveErrorCode(statusCode),
			Mensaje: this.unexpectedResponseMessage,
			data: null as T,
		};
	}

	private esEnvolturaRespuestaSp<T>(body: unknown): body is Partial<ISpResponse<T>> {
		return !!body &&
			typeof body === "object" &&
			"IdResultado" in (body as object) &&
			"Mensaje" in (body as object);
	}

	private toSpResponse<T>(
		body: Partial<ISpResponse<T>>,
		fallbackCode: number
	): ISpResponse<T> {
		return {
			IdResultado: typeof body.IdResultado === "number" ? body.IdResultado : fallbackCode,
			Mensaje: body.Mensaje || this.unexpectedResponseMessage,
			data: ("data" in body ? body.data : null) as T,
		};
	}

	private resolveErrorCode(statusCode: number): number {
		return statusCode >= 500 ? this.spServerErrorCode : this.spUserErrorCode;
	}

}
